import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Module-level counter for unique tooltip IDs
let tooltipIdCounter = 0;

class ToolTip extends Component {
  constructor() {
    super();
    this.wrapperRef = React.createRef();
    this.tooltipContentRef = React.createRef();
    this.triggerRef = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleShowTooltip = this.handleShowTooltip.bind(this);
    this.handleKeyboardEvents = this.handleKeyboardEvents.bind(this);
    this.state = {
      isTooltipShown: false,
      hasHover: false,
      openedViaKeyboard: false,
    };
    this.timer = undefined;
    // Generate unique ID once for this tooltip instance
    tooltipIdCounter += 1;
    this.tooltipId = `tooltip-${tooltipIdCounter}`;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyboardEvents);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyboardEvents);
  }

  handleClickOutside(event) {
    if (this.wrapperRef && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ isTooltipShown: false });
    }
  }

  handleKeyboardEvents(event) {
    if (event?.key === 'Escape' && this.state.isTooltipShown) {
      event.preventDefault();
      this.forceCloseTooltip();
    }
  }

  tooltipClose() {
    if (!this.timer || !this.state.hasHover) {
      this.setState({ isTooltipShown: false });
    }
  }

  forceCloseTooltip() {
    this.setState({
      isTooltipShown: false,
      hasHover: false,
      openedViaKeyboard: false,
    });
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    // Focus remains on trigger per WCAG guidelines
  }

  handleTooltipClose(e) {
    if (e) e.preventDefault();
    if (e && e?.type === 'mouseleave') {
      this.setState({ hasHover: false });
    } else if (e && e?.type === 'keydown') {
      // For keyboard events, force close by resetting hover state
      this.setState({ hasHover: false });
    }
    this.tooltipClose();
  }

  handleShowTooltip(e) {
    if (e && e.type !== 'touchstart') e.preventDefault();

    const isKeyboardEvent = e && e.type === 'keydown';
    this.setState({
      isTooltipShown: true,
      hasHover: true,
      openedViaKeyboard: isKeyboardEvent,
    });

    // Focus remains on trigger per WCAG guidelines
    // Content is announced via aria-describedby and live regions

    if (e && e.type === 'click') {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.tooltipClose();
        this.timer = undefined;
      }, 1000);
    }
  }

  render() {
    let {
      content,
      iconType,
      iconPosition,
      tooltipDisplay,
      showxonTooltip,
      attributeDescriptiveName,
    } = this.props;
    let tooltipContainer;
    const isClick = tooltipDisplay === 'Click' || tooltipDisplay === undefined;
    const isBoth = tooltipDisplay === 'Both';
    const isHover = tooltipDisplay === 'Hover';

    tooltipContainer = (
      <a
        ref={this.triggerRef}
        role="button"
        tabIndex={0}
        aria-describedby={
          this.state.isTooltipShown ? this.tooltipId : undefined
        }
        aria-expanded={this.state.isTooltipShown}
        aria-label={
          attributeDescriptiveName
            ? `${
                this.state.isTooltipShown ? 'Close' : 'Open'
              } tooltip for ${attributeDescriptiveName}`
            : `${this.state.isTooltipShown ? 'Close' : 'Open'} tooltip`
        }
        onClick={
          isClick || isBoth ? (e) => this.handleShowTooltip(e) : undefined
        }
        onKeyDown={
          isClick || isBoth
            ? (e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  e.preventDefault();
                  if (this.state.isTooltipShown) {
                    this.forceCloseTooltip();
                  } else {
                    this.handleShowTooltip(e);
                  }
                }
              }
            : undefined
        }
        onMouseEnter={
          isHover || isBoth ? (e) => this.handleShowTooltip(e) : undefined
        }
        onMouseLeave={
          isHover || isBoth ? (e) => this.handleTooltipClose(e) : undefined
        }
        onTouchStart={isBoth ? (e) => this.handleShowTooltip(e) : undefined}
      >
        <span className={`ltFormTooltipIcon${iconType}`}>
          <i />
        </span>
      </a>
    );

    return (
      <div
        ref={this.wrapperRef}
        className={`ltFormTooltip ltFormTooltip${iconPosition}`}
        style={window.LT5theme ? { display: 'inline' } : {}}
      >
        <div>{content && content.length > 0 ? tooltipContainer : null}</div>
        {this.state.isTooltipShown ? (
          <aside className="ltTooltipSidebar">
            <div>
              {showxonTooltip ? (
                <span
                  className="tooltipClose"
                  onClick={(e) => this.handleTooltipClose(e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      this.forceCloseTooltip();
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Close tooltip"
                >
                  &#10006;
                </span>
              ) : null}
              <div
                className="content"
                role="tooltip"
                id={this.tooltipId}
                ref={this.tooltipContentRef}
                tabIndex={-1}
              >
                {/*eslint-disable-next-line react/no-danger*/}
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </div>
            </div>
          </aside>
        ) : null}
      </div>
    );
  }
}
ToolTip.propTypes = {
  content: PropTypes.string,
  show: PropTypes.bool,
  dispatch: PropTypes.func,
  iconType: PropTypes.string,
  iconPosition: PropTypes.string,
  showxonTooltip: PropTypes.bool,
  tooltipDisplay: PropTypes.string,
  attributeDescriptiveName: PropTypes.string,
};
export default connect()(ToolTip);
